import { playAboutSplideAutoScroll } from './splide_set.js';

/**
 * 会社概要 .about02
 * - .about-imgBox__img：各画像をビューポートに入ったタイミングで表示
 * - #aboutSplide：ビューポートに入ったら AutoScroll ループ開始 → 1枚ずつフェードアップ
 */
let aboutImgObserver = null;
let aboutSplideRevealObserver = null;
let aboutSplideRevealStarted = false;

const ABOUT_SPLIDE_SELECTOR = '#aboutSplide';
const ABOUT_SPLIDE_STAGGER_MS = 280;
const ABOUT_SPLIDE_FADE_MS = 600;
const ABOUT_SPLIDE_IO_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0,
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function initAboutImages() {
  aboutImgObserver?.disconnect();
  aboutImgObserver = null;

  const images = document.querySelectorAll('.about.about02 .about-imgBox__img');
  if (!images.length) {
    return;
  }

  if (prefersReducedMotion()) {
    images.forEach((img) => img.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.15,
    }
  );

  images.forEach((img) => {
    img.classList.remove('is-visible');
    observer.observe(img);
  });

  aboutImgObserver = observer;
}

function getAboutSplideSlides(root) {
  return root.querySelectorAll('.aboutSplide-item:not(.is-clone)');
}

function resetAboutSplide(root) {
  root.classList.remove('is-slider-active', 'is-shown', 'is-all-visible');
  getAboutSplideSlides(root).forEach((slide) => {
    slide.style.removeProperty('--about-splide-delay');
  });
}

function revealAboutSplideSlides(root) {
  const slides = getAboutSplideSlides(root);

  slides.forEach((slide, index) => {
    slide.style.setProperty('--about-splide-delay', `${index * ABOUT_SPLIDE_STAGGER_MS}ms`);
  });

  if (prefersReducedMotion()) {
    root.classList.add('is-shown');
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.add('is-shown');
    });
  });
}

function isAboutSplideInViewport(root) {
  const rect = root.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

function getAboutSplideRevealDuration(root) {
  const count = getAboutSplideSlides(root).length;
  return Math.max(0, count - 1) * ABOUT_SPLIDE_STAGGER_MS + ABOUT_SPLIDE_FADE_MS;
}

function startAboutSplideShowAndScroll(root) {
  if (aboutSplideRevealStarted) {
    return;
  }
  aboutSplideRevealStarted = true;

  aboutSplideRevealObserver?.disconnect();
  aboutSplideRevealObserver = null;

  root.classList.add('is-slider-active');

  if (prefersReducedMotion()) {
    root.classList.add('is-shown', 'is-all-visible');
    return;
  }

  // 1. ループスクロールを先に開始
  playAboutSplideAutoScroll();

  // 2. ループ開始後にフェードアップ
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      revealAboutSplideSlides(root);
      setTimeout(() => {
        root.classList.add('is-all-visible');
      }, getAboutSplideRevealDuration(root));
    });
  });
}

export function initAboutSplideReveal() {
  aboutSplideRevealObserver?.disconnect();
  aboutSplideRevealObserver = null;
  aboutSplideRevealStarted = false;

  const root = document.querySelector(ABOUT_SPLIDE_SELECTOR);
  if (!root) {
    return;
  }

  resetAboutSplide(root);

  if (prefersReducedMotion()) {
    startAboutSplideShowAndScroll(root);
    return;
  }

  if (isAboutSplideInViewport(root)) {
    startAboutSplideShowAndScroll(root);
    return;
  }

  aboutSplideRevealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        startAboutSplideShowAndScroll(root);
      });
    },
    ABOUT_SPLIDE_IO_OPTIONS
  );

  aboutSplideRevealObserver.observe(root);
}

initAboutImages();
document.addEventListener('swup:replaced', initAboutImages);

initAboutSplideReveal();
document.addEventListener('swup:replaced', initAboutSplideReveal);
